"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import AuthenticatedLayout from "../../Layouts/AuthenticatedLayout";
import InfoSection from "../../Components/InfoSection";
import GroupedContainer from "../../Components/GroupedContainer";
import SecondaryButton from "../../Components/SecondaryButton";
import {
  TbAlertCircle,
  TbCalculator,
  TbCalendar,
  TbCar,
  TbCheck,
  TbChevronDown,
  TbCircleCheck,
  TbCurrencyDollar,
  TbEdit,
  TbFilter,
  TbInfoCircle,
  TbMail,
  TbPhone,
  TbPlus,
  TbRoute,
  TbSearch,
  TbTrash,
  TbUser,
  TbX,
} from "react-icons/tb";
import { ChevronLeft, ChevronRight } from "lucide-react";
import axios from "axios";

function Snackbar({ message, type = "success", onClose, duration = 5000 }) {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(onClose, 300);
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onClose]);

  const bgColor = {
    success: "bg-green-600 border-green-700",
    error: "bg-red-600 border-red-700",
    info: "bg-blue-600 border-blue-700",
    warning: "bg-amber-600 border-amber-700",
  }[type];

  const icon = {
    success: <TbCircleCheck className="w-5 h-5" />,
    error: <TbAlertCircle className="w-5 h-5" />,
    info: <TbInfoCircle className="w-5 h-5" />,
    warning: <TbInfoCircle className="w-5 h-5" />,
  }[type];

  return (
    <div
      className={`fixed bottom-4 right-4 z-[9999] transform transition-all duration-300 ${
        isVisible ? "translate-x-0 opacity-100" : "translate-x-full opacity-0"
      }`}
    >
      <div className={`${bgColor} text-white px-6 py-4 rounded-lg shadow-lg border max-w-sm flex items-start gap-3`}>
        <div className="flex-shrink-0 mt-0.5">{icon}</div>
        <div className="flex-1">
          <p className="text-sm font-medium">{message}</p>
        </div>
        <button
          onClick={() => {
            setIsVisible(false);
            setTimeout(onClose, 300);
          }}
          className="flex-shrink-0 text-white/80 hover:text-white ml-2"
        >
          <TbX className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}

function Lead() {
  const API_BASE_URL = "https://new.cajuu.app/api/lead";
  const QUOTE_PRICE_API_URL = "https://new.cajuu.app/api/quote-prices";
  const QUOTE_API_URL = "https://new.cajuu.app/api/quote";
  const GOOGLE_MAPS_SCRIPT_URL_API = "https://new.cajuu.app/api/google_maps_script_url";

  const [itemsPerPage, setItemsPerPage] = useState(10);

  const emptyLeadForm = {
    received_at: "",
    source: "",
    first_name: "",
    last_name: "",
    customer_email: "",
    customer_phone: "",
    origin_city: "",
    origin_state: "",
    origin_zip: "",
    destination_city: "",
    destination_state: "",
    destination_zip: "",
    vehicle_count: "1",
    vehicle_type: "",
    vehicle_year: "",
    vehicle_make: "",
    vehicle_model: "",
    vehicle_condition: "",
    move_date: "",
    price_low: "",
    price_medium: "",
    price_high: "",
    status: "new",
    customer_contacted_at: "",
  };

  const [leads, setLeads] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);

  const [loading, setLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editPriceLoading, setEditPriceLoading] = useState(false);
  const [editDistanceLoading, setEditDistanceLoading] = useState(false);
  const [editLeadDistanceMiles, setEditLeadDistanceMiles] = useState(null);
  const [editLeadPerMile, setEditLeadPerMile] = useState({
    low: null,
    medium: null,
    high: null,
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [paginationMeta, setPaginationMeta] = useState({
    total: 0,
    from: 0,
    to: 0,
    per_page: itemsPerPage,
  });

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");

  const [statusFilter, setStatusFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("");
  const [dateField, setDateField] = useState("received_at");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [currentEditLead, setCurrentEditLead] = useState(null);
  const [modalMode, setModalMode] = useState("edit");

  const [quoteModalOpen, setQuoteModalOpen] = useState(false);
  const [quoteLead, setQuoteLead] = useState(null);
  const [quotePayload, setQuotePayload] = useState(null);
  const [quoteResult, setQuoteResult] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteSaving, setQuoteSaving] = useState(false);
  const [quotePrices, setQuotePrices] = useState({
    low: "",
    medium: "",
    high: "",
  });
  const [quotePerMile, setQuotePerMile] = useState({
    low: null,
    medium: null,
    high: null,
  });
  const [saveAsQuoteAndEmail, setSaveAsQuoteAndEmail] = useState(true);
  const [googleMapsReady, setGoogleMapsReady] = useState(false);
  const [mapError, setMapError] = useState("");

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    type: "success",
  });

  const modalRef = useRef(null);
  const quoteModalRef = useRef(null);
  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const directionsRendererRef = useRef(null);

  const statusOptions = [
    { value: "new", label: "New", color: "bg-cyan-500/20 text-cyan-300 border border-cyan-500/40" },
    { value: "quoted", label: "Quoted", color: "bg-blue-500/20 text-blue-300 border border-blue-500/40" },
    { value: "contacted", label: "Contacted", color: "bg-purple-500/20 text-purple-300 border border-purple-500/40" },
    { value: "follow_up", label: "Follow Up", color: "bg-amber-500/20 text-amber-300 border border-amber-500/40" },
    { value: "converted", label: "Converted", color: "bg-emerald-500/20 text-emerald-300 border border-emerald-500/40" },
    { value: "lost", label: "Lost", color: "bg-red-500/20 text-red-300 border border-red-500/40" },
  ];

  const dateFieldOptions = [
    { value: "received_at", label: "Received At" },
    { value: "move_date", label: "Move Date" },
    { value: "customer_contacted_at", label: "Customer Contacted At" },
    { value: "created_at", label: "Created At" },
  ];

  const showSnackbar = (message, type = "success") => {
    setSnackbar({
      open: true,
      message,
      type,
    });
  };

  const closeSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const toDateKey = (dateStr) => {
    if (!dateStr || typeof dateStr !== "string") return "";
    return dateStr.split("T")[0].split(" ")[0];
  };

  const toDateTimeLocal = (dateStr) => {
    if (!dateStr || typeof dateStr !== "string") return "";

    const clean = dateStr.replace("T", " ").replace("Z", "");
    const [datePart, timePart = "00:00:00"] = clean.split(" ");

    if (!datePart) return "";

    const time = timePart.slice(0, 5);

    return `${datePart}T${time}`;
  };

  const fromDateTimeLocalToApi = (value) => {
    if (!value) return "";
    return value.replace("T", " ") + ":00";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";

    const key = toDateKey(dateString);
    const parts = key.split("-");

    if (parts.length !== 3) return "N/A";

    const y = Number(parts[0]);
    const m = Number(parts[1]) - 1;
    const d = Number(parts[2]);

    const dt = new Date(y, m, d);

    return dt.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";

    const clean = String(dateString).replace("T", " ").replace("Z", "");
    const [datePart, timePart = ""] = clean.split(" ");

    const dateFormatted = formatDate(datePart);

    if (!timePart) return dateFormatted;

    return `${dateFormatted} ${timePart.slice(0, 5)}`;
  };

  const parseNumericValue = (value) => {
    if (value === null || value === undefined || value === "") {
      return NaN;
    }

    if (typeof value === "number") {
      return value;
    }

    let normalizedValue = String(value).trim().replace(/[^0-9,.-]/g, "");

    if (
      !normalizedValue ||
      normalizedValue === "-" ||
      normalizedValue === "." ||
      normalizedValue === "," ||
      normalizedValue === "-." ||
      normalizedValue === "-,"
    ) {
      return NaN;
    }

    const hasComma = normalizedValue.includes(",");
    const hasDot = normalizedValue.includes(".");

    if (hasComma && hasDot) {
      const lastCommaIndex = normalizedValue.lastIndexOf(",");
      const lastDotIndex = normalizedValue.lastIndexOf(".");

      if (lastCommaIndex > lastDotIndex) {
        normalizedValue = normalizedValue.replace(/\./g, "").replace(/,/g, ".");
      } else {
        normalizedValue = normalizedValue.replace(/,/g, "");
      }
    } else if (hasComma) {
      const commaParts = normalizedValue.split(",");
      const lastPart = commaParts[commaParts.length - 1] || "";

      if (commaParts.length > 2 || lastPart.length === 3) {
        normalizedValue = normalizedValue.replace(/,/g, "");
      } else {
        normalizedValue = normalizedValue.replace(/,/g, ".");
      }
    }

    normalizedValue = normalizedValue.replace(/[^0-9.-]/g, "");

    if (!normalizedValue || normalizedValue === "-" || normalizedValue === "." || normalizedValue === "-.") {
      return NaN;
    }

    return parseFloat(normalizedValue);
  };

  const formatCurrency = (amount) => {
    const number = parseNumericValue(amount);

    if (Number.isNaN(number)) {
      return "$0.00";
    }

    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(number);
  };

  const formatNumber = (value, fractionDigits = 2) => {
    const number = parseNumericValue(value);

    if (Number.isNaN(number)) {
      return "N/A";
    }

    return number.toLocaleString("en-US", {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    });
  };

  const formatPriceInputValue = (value) => {
    const number = parseNumericValue(value);

    if (Number.isNaN(number)) {
      return "";
    }

    return number.toFixed(2);
  };

  const parseMaybeJson = (value) => {
    if (!value) return null;

    if (typeof value === "object") {
      return value;
    }

    if (typeof value !== "string") {
      return null;
    }

    try {
      return JSON.parse(value);
    } catch (error) {
      return null;
    }
  };

  const getDistanceFromQuoteCalculation = (calculation) => {
    if (!calculation) return null;

    const parsedCalculation = parseMaybeJson(calculation) || calculation;
    const distanceData = parsedCalculation?.input_parameters?.distance || {};

    const mileCandidates = [
      distanceData.effective_distance_miles,
      distanceData.distance_miles,
      distanceData.original_distance_miles,
      distanceData.route_distance_miles,
      distanceData.miles,
      distanceData.value_miles,
      distanceData.distance_text,
      parsedCalculation?.effective_distance_miles,
      parsedCalculation?.distance_miles,
      parsedCalculation?.original_distance_miles,
      parsedCalculation?.route_distance_miles,
      parsedCalculation?.miles,
      parsedCalculation?.distance?.effective_distance_miles,
      parsedCalculation?.distance?.distance_miles,
      parsedCalculation?.distance?.original_distance_miles,
      parsedCalculation?.distance?.route_distance_miles,
      parsedCalculation?.distance?.miles,
      parsedCalculation?.distance?.text,
      parsedCalculation?.distance_text,
      parsedCalculation?.route?.distance_miles,
      parsedCalculation?.route?.distance?.miles,
      parsedCalculation?.routes?.[0]?.distance_miles,
      parsedCalculation?.routes?.[0]?.distance?.miles,
      parsedCalculation?.vehicles?.[0]?.distance_miles,
      parsedCalculation?.vehicles?.[0]?.route_distance_miles,
    ];

    for (const candidate of mileCandidates) {
      const distance = parseNumericValue(candidate);

      if (!Number.isNaN(distance) && distance > 0) {
        return distance;
      }
    }

    const meterCandidates = [
      distanceData.distance_meters,
      distanceData.original_distance_meters,
      distanceData.value,
      distanceData.distance?.value,
      parsedCalculation?.distance_meters,
      parsedCalculation?.original_distance_meters,
      parsedCalculation?.distance?.value,
      parsedCalculation?.route?.distance?.value,
      parsedCalculation?.routes?.[0]?.distance?.value,
    ];

    for (const candidate of meterCandidates) {
      const meters = parseNumericValue(candidate);

      if (!Number.isNaN(meters) && meters > 0) {
        return meters / 1609.344;
      }
    }

    return null;
  };

  const getStatusStyle = (statusValue) => {
    const statusOption = statusOptions.find((opt) => opt.value === statusValue);

    if (!statusOption) {
      return "bg-gray-500/10 text-gray-400 border border-gray-500/20";
    }

    return statusOption.color;
  };

  const getStatusLabel = (statusValue) => {
    const statusOption = statusOptions.find((opt) => opt.value === statusValue);

    return statusOption ? statusOption.label : statusValue || "Unknown";
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");

    return {
      "Content-Type": "application/json",
      "Screen-Name": "/Lead",
      Authorization: `Bearer ${token}`,
    };
  };

  const unwrapPaginator = (responseData) => {
    return responseData?.data || {};
  };

  const normalizeVehicleTypeForQuote = (value) => {
    const normalized = String(value || "")
      .trim()
      .toLowerCase()
      .replace(/[\s_-]+/g, "");

    const aliases = {
      pickup: "pickuptruck",
      pickuptruck: "pickuptruck",
      truck: "pickuptruck",
      sportcar: "sportscar",
      sportscar: "sportscar",
      sports: "sportscar",
      wagon: "stationwagon",
      stationwagon: "stationwagon",
      minivan: "minivan",
      van: "van",
      suv: "suv",
      sedan: "sedan",
      coupe: "coupe",
      hatchback: "hatchback",
      crossover: "crossover",
      heavytruck: "heavytruck",
      fullsizetruck: "fullsizetruck",
      boxtruck: "boxtruck",
      other: "other",
    };

    return aliases[normalized] || normalized || "sedan";
  };

  const getOperableFromLead = (lead) => {
    const condition = String(lead?.vehicle_condition || "").trim().toLowerCase();

    if (
      condition.includes("inop") ||
      condition.includes("inoperable") ||
      condition.includes("non operable") ||
      condition.includes("non-operable") ||
      condition.includes("not running") ||
      condition.includes("does not run")
    ) {
      return 0;
    }

    return 1;
  };

  const buildLeadFullName = (lead) => {
    return `${lead?.first_name || ""} ${lead?.last_name || ""}`.trim();
  };

  const buildOriginAddress = (lead) => {
    return [lead?.origin_city, lead?.origin_state, lead?.origin_zip].filter(Boolean).join(", ").replace(", ", ", ");
  };

  const buildDestinationAddress = (lead) => {
    return [lead?.destination_city, lead?.destination_state, lead?.destination_zip].filter(Boolean).join(", ").replace(", ", ", ");
  };

  const buildQuotePricePayloadFromLead = (lead) => {
    return {
      pickup_zipcode: String(lead?.origin_zip || "").trim(),
      pickup_city: String(lead?.origin_city || "").trim(),
      pickup_state: String(lead?.origin_state || "").trim().toUpperCase(),
      delivery_zipcode: String(lead?.destination_zip || "").trim(),
      delivery_city: String(lead?.destination_city || "").trim(),
      delivery_state: String(lead?.destination_state || "").trim().toUpperCase(),
      transport_type: "Open",
      quote_vehicles: [
        {
          year: String(lead?.vehicle_year || "").trim(),
          make: String(lead?.vehicle_make || "").trim(),
          model: String(lead?.vehicle_model || "").trim(),
          type: normalizeVehicleTypeForQuote(lead?.vehicle_type),
          operable: getOperableFromLead(lead),
        },
      ],
    };
  };

  const buildLeadUpdatePayloadFromLead = (lead, prices = {}) => {
    return {
      received_at: lead?.received_at || "",
      source: lead?.source || "",
      first_name: lead?.first_name || "",
      last_name: lead?.last_name || "",
      customer_email: lead?.customer_email || "",
      customer_phone: lead?.customer_phone || "",

      origin_city: lead?.origin_city || "",
      origin_state: lead?.origin_state || "",
      origin_zip: lead?.origin_zip || "",

      destination_city: lead?.destination_city || "",
      destination_state: lead?.destination_state || "",
      destination_zip: lead?.destination_zip || "",

      vehicle_count: String(lead?.vehicle_count || "1"),
      vehicle_type: lead?.vehicle_type || "",
      vehicle_year: String(lead?.vehicle_year || ""),
      vehicle_make: lead?.vehicle_make || "",
      vehicle_model: lead?.vehicle_model || "",
      vehicle_condition: lead?.vehicle_condition || "",

      move_date: toDateKey(lead?.move_date) || "",

      price_low: prices.low === "" || prices.low === null || prices.low === undefined ? null : parseNumericValue(prices.low),
      price_medium: prices.medium === "" || prices.medium === null || prices.medium === undefined ? null : parseNumericValue(prices.medium),
      price_high: prices.high === "" || prices.high === null || prices.high === undefined ? null : parseNumericValue(prices.high),

      status: "quoted",
      customer_contacted_at: lead?.customer_contacted_at || null,
    };
  };

  const buildQuoteCreatePayloadFromLead = (lead) => {
    return {
      pickup_zipcode: String(lead?.origin_zip || "").trim(),
      pickup_city: String(lead?.origin_city || "").trim(),
      pickup_state: String(lead?.origin_state || "").trim().toUpperCase(),
      delivery_zipcode: String(lead?.destination_zip || "").trim(),
      delivery_city: String(lead?.destination_city || "").trim(),
      delivery_state: String(lead?.destination_state || "").trim().toUpperCase(),
      transport_type: "Open",
      pickup_date: toDateKey(lead?.move_date) || toDateKey(new Date().toISOString()),
      name: buildLeadFullName(lead) || lead?.customer_email || "Lead Customer",
      email: lead?.customer_email || "",
      phone: lead?.customer_phone || "",
      source: lead?.source || "lead",
      quote_vehicles: [
        {
          year: String(lead?.vehicle_year || "").trim(),
          make: String(lead?.vehicle_make || "").trim(),
          model: String(lead?.vehicle_model || "").trim(),
          type: normalizeVehicleTypeForQuote(lead?.vehicle_type),
          operable: getOperableFromLead(lead),
        },
      ],
    };
  };

  const getGoogleMapsUrlFromResponse = (responseData) => {
    if (typeof responseData === "string") {
      return responseData;
    }

    if (responseData?.url) {
      return responseData.url;
    }

    if (responseData?.script_url) {
      return responseData.script_url;
    }

    if (responseData?.data?.url) {
      return responseData.data.url;
    }

    if (responseData?.data?.script_url) {
      return responseData.data.script_url;
    }

    if (responseData?.data && typeof responseData.data === "string") {
      return responseData.data;
    }

    return "";
  };

  const loadGoogleMapsScript = async () => {
    if (window.google?.maps) {
      setGoogleMapsReady(true);
      return true;
    }

    const existingScript = document.querySelector('script[data-google-maps-cajuu="true"]');

    if (existingScript) {
      return new Promise((resolve) => {
        existingScript.addEventListener("load", () => {
          setGoogleMapsReady(true);
          resolve(true);
        });

        existingScript.addEventListener("error", () => {
          setMapError("Unable to load Google Maps script.");
          resolve(false);
        });
      });
    }

    try {
      const response = await axios.get(GOOGLE_MAPS_SCRIPT_URL_API, {
        headers: getAuthHeaders(),
      });

      const scriptUrl = getGoogleMapsUrlFromResponse(response.data);

      if (!scriptUrl) {
        setMapError("Google Maps script URL was not returned by the API.");
        return false;
      }

      return new Promise((resolve) => {
        const script = document.createElement("script");
        script.src = scriptUrl;
        script.async = true;
        script.defer = true;
        script.dataset.googleMapsCajuu = "true";

        script.onload = () => {
          setGoogleMapsReady(true);
          resolve(true);
        };

        script.onerror = () => {
          setMapError("Unable to load Google Maps script.");
          resolve(false);
        };

        document.head.appendChild(script);
      });
    } catch (error) {
      console.error("Error loading Google Maps script:", error);
      setMapError("Unable to load Google Maps script URL.");
      return false;
    }
  };

  const renderQuoteMap = async () => {
    if (!quoteLead || !mapContainerRef.current) return;

    setMapError("");

    const loaded = await loadGoogleMapsScript();

    if (!loaded || !window.google?.maps) {
      return;
    }

    const origin = `${quoteLead.origin_city || ""}, ${quoteLead.origin_state || ""} ${quoteLead.origin_zip || ""}`.trim();
    const destination = `${quoteLead.destination_city || ""}, ${quoteLead.destination_state || ""} ${
      quoteLead.destination_zip || ""
    }`.trim();

    if (!origin || !destination) {
      setMapError("Origin and destination are required to render the map.");
      return;
    }

    const map = new window.google.maps.Map(mapContainerRef.current, {
      center: { lat: 39.8283, lng: -98.5795 },
      zoom: 4,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: true,
    });

    mapInstanceRef.current = map;

    const directionsService = new window.google.maps.DirectionsService();

    const directionsRenderer = new window.google.maps.DirectionsRenderer({
      map,
      suppressMarkers: false,
      preserveViewport: false,
    });

    directionsRendererRef.current = directionsRenderer;

    directionsService.route(
      {
        origin,
        destination,
        travelMode: window.google.maps.TravelMode.DRIVING,
      },
      (result, status) => {
        if (status === "OK" && result) {
          directionsRenderer.setDirections(result);
          return;
        }

        setMapError("Unable to draw the route. Showing map without route.");
      }
    );
  };

  const extractGoogleMapsApiKeyFromScriptUrl = (scriptUrl) => {
    try {
      const url = new URL(scriptUrl);
      return url.searchParams.get("key") || "";
    } catch {
      return "";
    }
  };

  const [googleMapsApiKey, setGoogleMapsApiKey] = useState("");

  const fetchGoogleMapsApiKey = async () => {
    if (googleMapsApiKey) return googleMapsApiKey;

    const response = await axios.get(GOOGLE_MAPS_SCRIPT_URL_API, {
      headers: getAuthHeaders(),
    });

    const scriptUrl = getGoogleMapsUrlFromResponse(response.data);
    const apiKey = extractGoogleMapsApiKeyFromScriptUrl(scriptUrl);

    setGoogleMapsApiKey(apiKey);

    return apiKey;
  };

  const buildEmbedDirectionsUrl = () => {
    if (!googleMapsApiKey || !quoteLead) return "";

    const origin = `${quoteLead.origin_city || ""}, ${quoteLead.origin_state || ""} ${quoteLead.origin_zip || ""}, USA`;
    const destination = `${quoteLead.destination_city || ""}, ${quoteLead.destination_state || ""} ${quoteLead.destination_zip || ""}, USA`;

    const params = new URLSearchParams({
      key: googleMapsApiKey,
      origin,
      destination,
      mode: "driving",
    });

    return `https://www.google.com/maps/embed/v1/directions?${params.toString()}`;
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 450);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const fetchLeads = async (pageToFetch = currentPage) => {
    setLoading(true);

    try {
      let fromKey = toDateKey(dateFrom);
      let toKey = toDateKey(dateTo);

      if (fromKey && toKey && fromKey > toKey) {
        const tmp = fromKey;
        fromKey = toKey;
        toKey = tmp;
      }

      const params = {
        page: pageToFetch,
        per_page: itemsPerPage,
        sort_by: "received_at",
        sort_direction: "desc",
      };

      const q = (debouncedSearchQuery || "").trim();

      if (q) {
        params.search = q;
      }

      if (statusFilter !== "all") {
        params.status = statusFilter;
      }

      if (sourceFilter.trim() !== "") {
        params.source = sourceFilter.trim();
      }

      if (dateField) {
        params.date_field = dateField;
      }

      if (fromKey) {
        params.date_from = fromKey;
      }

      if (toKey) {
        params.date_to = toKey;
      }

      const response = await axios.get(API_BASE_URL, {
        params,
        headers: getAuthHeaders(),
      });

      const paginator = unwrapPaginator(response.data);
      const list = Array.isArray(paginator?.data) ? paginator.data : [];

      const apiLastPage = parseInt(paginator?.last_page || 1);
      const apiCurrentPage = parseInt(paginator?.current_page || pageToFetch);

      setTotalPages(apiLastPage || 1);

      setPaginationMeta({
        total: parseInt(paginator?.total || 0),
        from: parseInt(paginator?.from || 0) || 0,
        to: parseInt(paginator?.to || 0) || 0,
        per_page: parseInt(paginator?.per_page || itemsPerPage) || itemsPerPage,
      });

      if (apiLastPage > 0 && pageToFetch > apiLastPage) {
        setCurrentPage(apiLastPage);
      } else {
        setCurrentPage(apiCurrentPage);
      }

      setLeads(list);
      setFilteredLeads(list);
    } catch (error) {
      console.error("Error fetching leads:", error);

      setLeads([]);
      setFilteredLeads([]);
      setTotalPages(1);
      setPaginationMeta({
        total: 0,
        from: 0,
        to: 0,
        per_page: itemsPerPage,
      });

      let errorMessage = "Erro ao carregar leads";

      if (error.response) {
        errorMessage = error.response.data?.message || error.response.data?.error || errorMessage;
      }

      showSnackbar(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeads(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, debouncedSearchQuery, statusFilter, sourceFilter, dateField, dateFrom, dateTo, itemsPerPage]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target) && editModalOpen) {
        setEditModalOpen(false);
        setCurrentEditLead(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [editModalOpen]);

  useEffect(() => {
    if (!quoteModalOpen || !quoteLead) return;

    fetchGoogleMapsApiKey().catch(() => {
      setMapError("Unable to load Google Maps API key.");
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteModalOpen, quoteLead]);

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const clearSearch = () => {
    setSearchQuery("");
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSearchQuery("");
    setStatusFilter("all");
    setSourceFilter("");
    setDateField("received_at");
    setDateFrom("");
    setDateTo("");
    setCurrentPage(1);
    showSnackbar("Filters clean", "info");
  };

  const handleCreate = () => {
    setModalMode("create");
    setCurrentEditLead({
      ...emptyLeadForm,
      received_at: toDateTimeLocal(new Date().toISOString()),
    });
    setEditLeadDistanceMiles(null);
    setEditLeadPerMile({
      low: null,
      medium: null,
      high: null,
    });
    setEditModalOpen(true);
  };

  const canRequestQuoteDistanceForLead = (lead) => {
    const requiredFields = [
      "origin_city",
      "origin_state",
      "origin_zip",
      "destination_city",
      "destination_state",
      "destination_zip",
      "vehicle_year",
      "vehicle_make",
      "vehicle_model",
      "vehicle_type",
    ];

    return requiredFields.every((field) => String(lead?.[field] ?? "").trim());
  };

  const fetchEditLeadDistance = async (lead) => {
    if (!lead || !canRequestQuoteDistanceForLead(lead)) {
      setEditLeadDistanceMiles(null);
      return;
    }

    setEditDistanceLoading(true);

    try {
      const payload = buildQuotePricePayloadFromLead(lead);

      const response = await axios.post(QUOTE_PRICE_API_URL, payload, {
        headers: getAuthHeaders(),
      });

      const data = response.data || {};
      const calculatedDistance = getDistanceFromQuoteCalculation(data);

      setEditLeadDistanceMiles(calculatedDistance);

      setCurrentEditLead((prev) => {
        if (!prev || String(prev.id ?? "") !== String(lead.id ?? "")) {
          return prev;
        }

        return {
          ...prev,
          effective_distance_miles: calculatedDistance ?? prev.effective_distance_miles ?? null,
          _edit_distance_result: data,
        };
      });
    } catch (error) {
      console.error("Error calculating edit lead distance:", error);
      setEditLeadDistanceMiles(null);
    } finally {
      setEditDistanceLoading(false);
    }
  };

  const handleEdit = (leadId) => {
    const lead = leads.find((item) => String(item.id) === String(leadId));

    if (!lead) return;

    const preparedLead = {
      ...lead,
      received_at: toDateTimeLocal(lead.received_at),
      move_date: toDateKey(lead.move_date),
      customer_contacted_at: toDateTimeLocal(lead.customer_contacted_at),
      price_low: lead.price_low ?? "",
      price_medium: lead.price_medium ?? "",
      price_high: lead.price_high ?? "",
      status: lead.status || "new",
      _quote_price_result:
        parseMaybeJson(lead.quote_result) ||
        parseMaybeJson(lead.quote_price_result) ||
        parseMaybeJson(lead.quote_calculation_result) ||
        parseMaybeJson(lead.calculated_prices) ||
        parseMaybeJson(lead.quoteResult) ||
        parseMaybeJson(lead.quote?.quote_result) ||
        parseMaybeJson(lead.quote?.quote_price_result) ||
        parseMaybeJson(lead.quote?.calculated_prices) ||
        lead.quote_result ||
        lead.quote_price_result ||
        lead.quote_calculation_result ||
        lead.calculated_prices ||
        lead.quoteResult ||
        lead.quote?.quote_result ||
        lead.quote?.quote_price_result ||
        lead.quote?.calculated_prices ||
        null,
    };

    setModalMode("edit");
    setCurrentEditLead(preparedLead);
    setEditLeadDistanceMiles(null);
    setEditLeadPerMile({
      low: null,
      medium: null,
      high: null,
    });
    setEditModalOpen(true);
    fetchEditLeadDistance(preparedLead);
  };

  const validateLeadForm = () => {
    const requiredFields = [
      ["received_at", "Received At"],
      ["source", "Source"],
      ["first_name", "First Name"],
      ["last_name", "Last Name"],
      ["customer_email", "Customer Email"],
      ["customer_phone", "Customer Phone"],
      ["origin_city", "Origin City"],
      ["origin_state", "Origin State"],
      ["origin_zip", "Origin Zip"],
      ["destination_city", "Destination City"],
      ["destination_state", "Destination State"],
      ["destination_zip", "Destination Zip"],
      ["vehicle_count", "Vehicle Count"],
      ["vehicle_type", "Vehicle Type"],
      ["vehicle_year", "Vehicle Year"],
      ["vehicle_make", "Vehicle Make"],
      ["vehicle_model", "Vehicle Model"],
      ["vehicle_condition", "Vehicle Condition"],
      ["move_date", "Move Date"],
    ];

    for (const [field, label] of requiredFields) {
      if (!String(currentEditLead?.[field] ?? "").trim()) {
        showSnackbar(`${label} is required`, "error");
        return false;
      }
    }

    return true;
  };

  const buildLeadPayload = () => {
    return {
      received_at: fromDateTimeLocalToApi(currentEditLead.received_at),
      source: currentEditLead.source || "",
      first_name: currentEditLead.first_name || "",
      last_name: currentEditLead.last_name || "",
      customer_email: currentEditLead.customer_email || "",
      customer_phone: currentEditLead.customer_phone || "",

      origin_city: currentEditLead.origin_city || "",
      origin_state: currentEditLead.origin_state || "",
      origin_zip: currentEditLead.origin_zip || "",

      destination_city: currentEditLead.destination_city || "",
      destination_state: currentEditLead.destination_state || "",
      destination_zip: currentEditLead.destination_zip || "",

      vehicle_count: String(currentEditLead.vehicle_count || ""),
      vehicle_type: currentEditLead.vehicle_type || "",
      vehicle_year: String(currentEditLead.vehicle_year || ""),
      vehicle_make: currentEditLead.vehicle_make || "",
      vehicle_model: currentEditLead.vehicle_model || "",
      vehicle_condition: currentEditLead.vehicle_condition || "",

      move_date: currentEditLead.move_date || "",

      price_low: currentEditLead.price_low === "" || currentEditLead.price_low === null ? null : parseNumericValue(currentEditLead.price_low),
      price_medium:
        currentEditLead.price_medium === "" || currentEditLead.price_medium === null ? null : parseNumericValue(currentEditLead.price_medium),
      price_high: currentEditLead.price_high === "" || currentEditLead.price_high === null ? null : parseNumericValue(currentEditLead.price_high),

      status: currentEditLead.status || "new",
      customer_contacted_at: currentEditLead.customer_contacted_at
        ? fromDateTimeLocalToApi(currentEditLead.customer_contacted_at)
        : null,
    };
  };

  const handleSaveLead = async () => {
    if (!currentEditLead) return;

    if (!validateLeadForm()) return;

    setIsSaving(true);

    try {
      const payload = buildLeadPayload();

      let response;

      if (modalMode === "create") {
        response = await axios.post(API_BASE_URL, payload, {
          headers: getAuthHeaders(),
        });
      } else {
        response = await axios.post(`${API_BASE_URL}/${currentEditLead.id}`, payload, {
          headers: getAuthHeaders(),
        });
      }

      if (response.status === 200 || response.status === 201) {
        setEditModalOpen(false);
        setCurrentEditLead(null);

        showSnackbar(modalMode === "create" ? "Lead criado com sucesso!" : "Lead atualizado com sucesso!", "success");

        await fetchLeads(currentPage);
      } else {
        throw new Error("Falha ao salvar lead");
      }
    } catch (error) {
      console.error("Error saving lead:", error);

      let errorMessage = "Erro ao salvar lead";

      if (error.response) {
        errorMessage =
          error.response.data?.message ||
          error.response.data?.error ||
          error.response.data?.data ||
          "Erro desconhecido da API";
      } else if (error.request) {
        errorMessage = "Erro de rede. Verifique sua conexão.";
      }

      showSnackbar(String(errorMessage), "error");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (leadId) => {
    const lead = leads.find((item) => String(item.id) === String(leadId));

    if (!lead) return;

    const customerName = `${lead.first_name || ""} ${lead.last_name || ""}`.trim() || lead.customer_email;

    if (!window.confirm(`Are you sure you want to delete lead #${lead.id} - ${customerName}? This action can be restored later.`)) {
      return;
    }

    try {
      await axios.delete(`${API_BASE_URL}/${leadId}`, {
        headers: getAuthHeaders(),
      });

      showSnackbar("Lead deletado com sucesso!", "success");

      await fetchLeads(currentPage);
    } catch (error) {
      console.error("Error deleting lead:", error);

      let errorMessage = "Erro ao deletar lead";

      if (error.response) {
        errorMessage = error.response.data?.message || error.response.data?.error || errorMessage;
      }

      showSnackbar(errorMessage, "error");
    }
  };

  const validateQuoteLead = (lead) => {
    const requiredFields = [
      ["origin_city", "Origin City"],
      ["origin_state", "Origin State"],
      ["destination_city", "Destination City"],
      ["destination_state", "Destination State"],
      ["vehicle_year", "Vehicle Year"],
      ["vehicle_make", "Vehicle Make"],
      ["vehicle_model", "Vehicle Model"],
      ["vehicle_type", "Vehicle Type"],
      ["customer_email", "Customer Email"],
    ];

    for (const [field, label] of requiredFields) {
      if (!String(lead?.[field] ?? "").trim()) {
        showSnackbar(`${label} is required to calculate quote price.`, "error");
        return false;
      }
    }

    return true;
  };

  const getQuoteDistanceMiles = () => {
    const distanceData = quoteResult?.input_parameters?.distance || {};

    const rawDistance =
      distanceData.effective_distance_miles ??
      distanceData.distance_miles ??
      distanceData.original_distance_miles ??
      quoteResult?.effective_distance_miles ??
      quoteResult?.distance_miles ??
      quoteResult?.original_distance_miles ??
      null;

    const distance = parseNumericValue(rawDistance);

    if (Number.isNaN(distance) || distance <= 0) {
      return null;
    }

    return distance;
  };

  const calculatePricePerMile = (price, distanceMiles) => {
    const numericPrice = parseNumericValue(price);

    if (Number.isNaN(numericPrice) || numericPrice < 0 || !distanceMiles || distanceMiles <= 0) {
      return null;
    }

    return numericPrice / distanceMiles;
  };

  const handleOpenQuoteModal = async (lead) => {
    if (!validateQuoteLead(lead)) return;

    const payload = buildQuotePricePayloadFromLead(lead);

    setQuoteLead(lead);
    setQuotePayload(payload);
    setQuoteResult(null);
    setQuotePrices({
      low: "",
      medium: "",
      high: "",
    });
    setQuotePerMile({
      low: null,
      medium: null,
      high: null,
    });
    setSaveAsQuoteAndEmail(true);
    setMapError("");
    setQuoteModalOpen(true);
    setQuoteLoading(true);

    try {
      const response = await axios.post(QUOTE_PRICE_API_URL, payload, {
        headers: getAuthHeaders(),
      });

      const data = response.data || {};
      const totalPrices = data.total_prices || {};
      const firstVehiclePrices = data.vehicles?.[0]?.prices || {};

      setQuoteResult(data);

      setQuotePrices({
        low: formatPriceInputValue(totalPrices.low),
        medium: formatPriceInputValue(totalPrices.medium),
        high: formatPriceInputValue(totalPrices.high),
      });

      setQuotePerMile({
        low: firstVehiclePrices.low_per_mile ?? null,
        medium: firstVehiclePrices.medium_per_mile ?? null,
        high: firstVehiclePrices.high_per_mile ?? null,
      });

      if (data.manual_pricing_required) {
        showSnackbar("Manual pricing is required for this lead.", "warning");
      } else {
        // showSnackbar("Automatic quote price calculated successfully.", "success");
      }
    } catch (error) {
      console.error("Error calculating quote price:", error);

      let errorMessage = "Error calculating automatic quote price.";

      if (error.response) {
        errorMessage = error.response.data?.message || error.response.data?.error || errorMessage;
      }

      showSnackbar(errorMessage, "error");
    } finally {
      setQuoteLoading(false);
    }
  };

  const closeQuoteModal = () => {
    setQuoteModalOpen(false);
    setQuoteLead(null);
    setQuotePayload(null);
    setQuoteResult(null);
    setQuotePrices({
      low: "",
      medium: "",
      high: "",
    });
    setQuotePerMile({
      low: null,
      medium: null,
      high: null,
    });
    setSaveAsQuoteAndEmail(true);
    setMapError("");
  };

  const validateQuotePrices = () => {
    const low = parseNumericValue(quotePrices.low);
    const medium = parseNumericValue(quotePrices.medium);
    const high = parseNumericValue(quotePrices.high);

    if (Number.isNaN(low) || low < 0) {
      showSnackbar("Low price must be a valid number.", "error");
      return false;
    }

    if (Number.isNaN(medium) || medium < 0) {
      showSnackbar("Medium price must be a valid number.", "error");
      return false;
    }

    if (Number.isNaN(high) || high < 0) {
      showSnackbar("High price must be a valid number.", "error");
      return false;
    }

    return true;
  };

  const handleSaveQuotePrice = async () => {
    if (!quoteLead) return;

    if (!validateQuotePrices()) return;

    setQuoteSaving(true);

    try {
      await axios.post(
        `${API_BASE_URL}/${quoteLead.id}/save-quote-price`,
        {
          price_low: parseNumericValue(quotePrices.low),
          price_medium: parseNumericValue(quotePrices.medium),
          price_high: parseNumericValue(quotePrices.high),
          save_as_quote_and_email: saveAsQuoteAndEmail,
          quote_payload: quotePayload,
          quote_result: quoteResult,
        },
        {
          headers: getAuthHeaders(),
        }
      );

      showSnackbar(
        saveAsQuoteAndEmail
          ? "Prices saved, Quote created, and email sent successfully."
          : "Prices saved successfully.",
        "success"
      );

      closeQuoteModal();

      await fetchLeads(currentPage);
    } catch (error) {
      console.error("Error saving quote price:", error);

      let errorMessage = "Error saving quote price.";

      if (error.response) {
        errorMessage =
          error.response.data?.message ||
          error.response.data?.error ||
          error.response.data?.data ||
          "Unknown API error.";
      }

      showSnackbar(String(errorMessage), "error");
    } finally {
      setQuoteSaving(false);
    }
  };

  const handleCalculateEditLeadPrices = async () => {
    if (!currentEditLead) return;

    if (!validateQuoteLead(currentEditLead)) return;

    setEditPriceLoading(true);

    try {
      const payload = buildQuotePricePayloadFromLead(currentEditLead);

      const response = await axios.post(QUOTE_PRICE_API_URL, payload, {
        headers: getAuthHeaders(),
      });

      const data = response.data || {};
      const totalPrices = data.total_prices || {};
      const calculatedDistance = getDistanceFromQuoteCalculation(data);

      setEditLeadDistanceMiles(calculatedDistance);

      setCurrentEditLead((prev) => ({
        ...prev,
        price_low: formatPriceInputValue(totalPrices.low),
        price_medium: formatPriceInputValue(totalPrices.medium),
        price_high: formatPriceInputValue(totalPrices.high),
        effective_distance_miles: calculatedDistance ?? prev?.effective_distance_miles ?? prev?.distance_miles ?? prev?.original_distance_miles ?? null,
        _quote_price_result: data,
      }));

      if (data.manual_pricing_required) {
        showSnackbar("Automatic calculation returned manual pricing required. Review the values before saving.", "warning");
      } else {
        showSnackbar("Automatic prices calculated for this lead.", "success");
      }
    } catch (error) {
      console.error("Error calculating edit lead prices:", error);

      let errorMessage = "Error calculating automatic prices.";

      if (error.response) {
        errorMessage = error.response.data?.message || error.response.data?.error || errorMessage;
      }

      showSnackbar(String(errorMessage), "error");
    } finally {
      setEditPriceLoading(false);
    }
  };

  const updateCurrentLead = (field, value) => {
    setCurrentEditLead((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const updateQuotePrice = (field, value) => {
    setQuotePrices((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  useEffect(() => {
    if (!quoteModalOpen) return;

    const distanceMiles = getQuoteDistanceMiles();

    setQuotePerMile({
      low: calculatePricePerMile(quotePrices.low, distanceMiles),
      medium: calculatePricePerMile(quotePrices.medium, distanceMiles),
      high: calculatePricePerMile(quotePrices.high, distanceMiles),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteModalOpen, quotePrices.low, quotePrices.medium, quotePrices.high, quoteResult]);

  const getEditLeadDistanceMilesFromLeadData = () => {
    if (!currentEditLead) return null;

    const calculationDistance =
      getDistanceFromQuoteCalculation(currentEditLead._edit_distance_result) ??
      getDistanceFromQuoteCalculation(currentEditLead._quote_price_result) ??
      getDistanceFromQuoteCalculation(currentEditLead.quote_result) ??
      getDistanceFromQuoteCalculation(currentEditLead.quote_price_result) ??
      getDistanceFromQuoteCalculation(currentEditLead.quote_calculation_result) ??
      getDistanceFromQuoteCalculation(currentEditLead.calculated_prices) ??
      getDistanceFromQuoteCalculation(currentEditLead.quoteResult) ??
      getDistanceFromQuoteCalculation(currentEditLead.quote?.quote_result) ??
      getDistanceFromQuoteCalculation(currentEditLead.quote?.quote_price_result) ??
      getDistanceFromQuoteCalculation(currentEditLead.quote?.calculated_prices) ??
      null;

    if (calculationDistance) {
      return calculationDistance;
    }

    const rawDistance =
      currentEditLead.effective_distance_miles ??
      currentEditLead.distance_miles ??
      currentEditLead.original_distance_miles ??
      currentEditLead.original_distance_value ??
      currentEditLead.route_distance_miles ??
      currentEditLead.quote?.effective_distance_miles ??
      currentEditLead.quote?.distance_miles ??
      currentEditLead.quote?.original_distance_miles ??
      currentEditLead.quote_data?.effective_distance_miles ??
      currentEditLead.quote_data?.distance_miles ??
      currentEditLead.quote_data?.original_distance_miles ??
      null;

    const distance = parseNumericValue(rawDistance);

    if (Number.isNaN(distance) || distance <= 0) {
      return null;
    }

    return distance;
  };

  const editLeadDistanceForPreview = editLeadDistanceMiles || getEditLeadDistanceMilesFromLeadData();

  useEffect(() => {
    if (!editModalOpen || !currentEditLead) {
      setEditLeadPerMile({
        low: null,
        medium: null,
        high: null,
      });
      return;
    }

    setEditLeadPerMile({
      low: calculatePricePerMile(currentEditLead.price_low, editLeadDistanceForPreview),
      medium: calculatePricePerMile(currentEditLead.price_medium, editLeadDistanceForPreview),
      high: calculatePricePerMile(currentEditLead.price_high, editLeadDistanceForPreview),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    editModalOpen,
    currentEditLead?.price_low,
    currentEditLead?.price_medium,
    currentEditLead?.price_high,
    currentEditLead?._edit_distance_result,
    currentEditLead?._quote_price_result,
    currentEditLead?.effective_distance_miles,
    currentEditLead?.distance_miles,
    currentEditLead?.original_distance_miles,
    currentEditLead?.route_distance_miles,
    editLeadDistanceMiles,
    editLeadDistanceForPreview,
  ]);

  const currentLeads = filteredLeads;

  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const nextPage = () => setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  const prevPage = () => setCurrentPage((prev) => Math.max(prev - 1, 1));

  const uniqueSources = useMemo(() => {
    const values = leads.map((lead) => lead.source).filter(Boolean);
    return [...new Set(values)];
  }, [leads]);

  const quoteDistanceData = quoteResult?.input_parameters?.distance || {};
  const quoteVehicle = quotePayload?.quote_vehicles?.[0] || {};
  const quotePricingDisclaimer = quoteResult?.pricing_disclaimer || quoteResult?.pricing_adjustment?.disclaimer || "";

  return (
    <AuthenticatedLayout>
      <div className="text-white text-base sm:text-lg md:text-xl lg:text-2xl p-4 sm:p-6 md:p-8">
        <GroupedContainer>
          <InfoSection title="LEADS" description="Leads received by email and extracted automatically" />
        </GroupedContainer>

        <div className="bg-[#1c1b19] p-4 sm:p-6 rounded-xl shadow-sm text-white border border-zinc-600 mt-4 mb-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center mb-4">
            <h2 className="text-lg sm:text-xl font-bold">Filters</h2>

            <div className="flex flex-wrap gap-2">
              <button onClick={clearFilters} className="text-sm text-gray-400 hover:text-white flex items-center gap-1">
                <TbX className="w-4 h-4" /> Clear Filters
              </button>

              <button
                onClick={handleCreate}
                className="px-4 py-2 bg-amber-600 text-black rounded-lg hover:bg-amber-700 transition-colors flex items-center gap-2 text-sm font-medium"
              >
                <TbPlus className="w-4 h-4" />
                Add Lead
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="relative lg:col-span-2">
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                <TbSearch className="text-white text-lg" />
              </div>

              <input
                type="text"
                placeholder="Search name, email, phone, city, vehicle..."
                value={searchQuery}
                onChange={handleSearch}
                className="w-full pl-10 pr-10 bg-transparent placeholder:text-white py-2 sm:py-3 text-sm border text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
              />

              {searchQuery && (
                <button
                  onClick={clearSearch}
                  className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-white"
                >
                  <TbX className="text-white" />
                </button>
              )}
            </div>

            <div className="relative">
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                <TbFilter className="text-amber-500 text-lg" />
              </div>

              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full pl-10 pr-12 bg-transparent text-white py-2 sm:py-3 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all appearance-none"
              >
                <option value="all" className="bg-[#1c1b19]">
                  All Status
                </option>

                {statusOptions.map((option) => (
                  <option key={option.value} value={option.value} className="bg-[#1c1b19]">
                    {option.label}
                  </option>
                ))}
              </select>

              <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                <TbChevronDown className="text-white text-lg" />
              </div>
            </div>

            <div className="relative">
              <input
                type="text"
                placeholder="Source"
                value={sourceFilter}
                onChange={(e) => {
                  setSourceFilter(e.target.value);
                  setCurrentPage(1);
                }}
                list="lead-sources"
                className="w-full px-4 bg-transparent placeholder:text-white py-2 sm:py-3 text-sm border text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
              />

              <datalist id="lead-sources">
                {uniqueSources.map((source) => (
                  <option key={source} value={source} />
                ))}
              </datalist>
            </div>

            <div className="relative">
              <select
                value={dateField}
                onChange={(e) => {
                  setDateField(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full px-4 pr-10 bg-transparent text-white py-2 sm:py-3 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all appearance-none"
              >
                {dateFieldOptions.map((option) => (
                  <option key={option.value} value={option.value} className="bg-[#1c1b19]">
                    {option.label}
                  </option>
                ))}
              </select>

              <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                <TbChevronDown className="text-white text-lg" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2 lg:col-span-2">
              <div className="relative">
                <span className="absolute -top-2 left-3 bg-[#1c1b19] px-1 text-xs text-gray-400">From</span>

                <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                  <TbCalendar className="text-blue-500 text-lg" />
                </div>

                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => {
                    setDateFrom(e.target.value);
                    setCurrentPage(1);
                  }}
                  onFocus={(e) => e.target.showPicker?.()}
                  style={{ colorScheme: "dark" }}
                  className="w-full pl-10 pr-10 bg-transparent text-white py-2 sm:py-3 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                />
              </div>

              <div className="relative">
                <span className="absolute -top-2 left-3 bg-[#1c1b19] px-1 text-xs text-gray-400">To</span>

                <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                  <TbCalendar className="text-blue-500 text-lg" />
                </div>

                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => {
                    setDateTo(e.target.value);
                    setCurrentPage(1);
                  }}
                  onFocus={(e) => e.target.showPicker?.()}
                  style={{ colorScheme: "dark" }}
                  className="w-full pl-10 pr-10 bg-transparent text-white py-2 sm:py-3 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                />
              </div>
            </div>

            <div className="relative">
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="w-full px-4 pr-10 bg-transparent text-white py-2 sm:py-3 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all appearance-none"
              >
                <option value={10} className="bg-[#1c1b19]">10 / page</option>
                <option value={20} className="bg-[#1c1b19]">20 / page</option>
                <option value={50} className="bg-[#1c1b19]">50 / page</option>
                <option value={100} className="bg-[#1c1b19]">100 / page</option>
              </select>

              <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                <TbChevronDown className="text-white text-lg" />
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 overflow-x-auto rounded-xl border border-zinc-600">
          <table className="min-w-full divide-y divide-zinc-600">
            <thead className="bg-[#1c1b19]">
              <tr>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Status</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Received</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Customer</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Route</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider max-w-[120px]">Vehicle</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Move</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Prices</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>

            <tbody className="bg-[#1c1b19]/50 divide-y divide-zinc-600">
              {loading ? (
                <tr>
                  <td colSpan="8" className="px-6 py-8 text-center">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
                    </div>
                    <p className="mt-2 text-gray-400">Loading leads...</p>
                  </td>
                </tr>
              ) : currentLeads.length > 0 ? (
                currentLeads.map((lead) => (
                  <tr key={lead.id} className="hover:bg-zinc-800/30 transition-colors">
                    <td className="px-3 py-3 whitespace-nowrap">
                      <span className={`px-2.5 py-1 text-xs rounded-full ${getStatusStyle(lead.status)}`}>
                        {getStatusLabel(lead.status)}
                      </span>
                    </td>

                    <td className="px-3 py-3 whitespace-nowrap text-xs text-gray-300">
                      <div className="font-medium text-white">{formatDateTime(lead.received_at)}</div>
                      <div className="text-[11px] text-gray-400">{lead.source || "N/A"}</div>
                    </td>

                    <td className="px-3 py-3 whitespace-nowrap text-xs text-gray-300">
                      <div className="space-y-1.5">
                        <div className="flex items-center gap-1.5 font-medium text-white">
                          <TbUser className="w-3.5 h-3.5 text-amber-400 shrink-0" />
                          <span>
                            {`${lead.first_name || ""} ${lead.last_name || ""}`.trim() || "N/A"}
                          </span>
                        </div>

                        <div className="flex items-center gap-1.5 text-[11px] text-gray-400">
                          <TbMail className="w-3.5 h-3.5 text-blue-300 shrink-0" />
                          <span>{lead.customer_email || "N/A"}</span>
                        </div>

                        <div className="flex items-center gap-1.5 text-[11px] text-gray-400">
                          <TbPhone className="w-3.5 h-3.5 text-green-300 shrink-0" />
                          <span>{lead.customer_phone || "N/A"}</span>
                        </div>
                      </div>
                    </td>

                    <td className="px-3 py-3 text-xs text-gray-300 min-w-[210px]">
                      <div className="space-y-2">
                        <div className="flex items-start gap-2">
                          <span className="flex h-2.5 w-2.5 mt-1.5 rounded-full bg-green-500 ring-2 ring-green-200 flex-shrink-0"></span>
                          <span className="text-white leading-snug">
                            {[lead.origin_city, lead.origin_state].filter(Boolean).join(", ")} {lead.origin_zip || ""}
                          </span>
                        </div>

                        <div className="flex items-start gap-2">
                          <span className="flex h-2.5 w-2.5 mt-1.5 rounded-full bg-red-500 ring-2 ring-red-200 flex-shrink-0"></span>
                          <span className="text-white leading-snug">
                            {[lead.destination_city, lead.destination_state].filter(Boolean).join(", ")} {lead.destination_zip || ""}
                          </span>
                        </div>
                      </div>
                    </td>

                    <td className="px-3 py-3 text-xs text-gray-300 max-w-[190px]">
                      <div className="leading-tight">
                        <div className="font-medium text-white truncate">
                          {[lead.vehicle_year, lead.vehicle_make, lead.vehicle_model].filter(Boolean).join(" ") || "N/A"}
                        </div>

                        <div className="mt-1 text-[11px] text-gray-400 truncate">{lead.vehicle_type || "N/A"}</div>

                        {String(lead.vehicle_condition || "").toLowerCase() === "yes" ||
                        String(lead.vehicle_condition || "").toLowerCase() === "operable" ? (
                          <div className="mt-1 text-[11px] font-bold text-green-400">OP</div>
                        ) : (
                          <div className="mt-1 text-[11px] font-bold text-red-400">INOP</div>
                        )}
                      </div>
                    </td>

                    <td className="px-3 py-3 whitespace-nowrap text-xs text-gray-300">
                      {formatDate(lead.move_date)}
                    </td>

                    <td className="px-3 py-3 whitespace-nowrap text-xs text-gray-300">
                      <div className="text-green-400">Low: {formatCurrency(lead.price_low)}</div>
                      <div className="text-amber-400">Mid: {formatCurrency(lead.price_medium)}</div>
                      <div className="text-red-400">High: {formatCurrency(lead.price_high)}</div>
                    </td>

                    <td className="px-3 py-3 whitespace-nowrap">
                      <div className="flex items-center space-x-1">
                        <button
                          onClick={() => handleOpenQuoteModal(lead)}
                          className="p-1.5 text-gray-400 hover:text-green-400 hover:bg-green-500/20 rounded-lg transition-colors"
                          title="Calculate quote"
                        >
                          <TbCalculator className="w-4 h-4" />
                        </button>

                        <button
                          onClick={() => handleEdit(lead.id)}
                          className="p-1.5 text-gray-400 hover:text-amber-500 hover:bg-zinc-700/50 rounded-lg transition-colors"
                          title="Edit lead"
                        >
                          <TbEdit className="w-4 h-4" />
                        </button>

                        <button
                          onClick={() => handleDelete(lead.id)}
                          className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-500/20 rounded-lg transition-colors"
                          title="Delete lead"
                        >
                          <TbTrash className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="px-6 py-8 text-center text-gray-400">
                    No leads found matching your filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {paginationMeta.total > 0 && (
          <>
            <div className="flex items-center justify-center space-x-2 mt-6">
              <SecondaryButton
                variant="outline"
                size="sm"
                onClick={prevPage}
                disabled={currentPage === 1}
                className="text-white border-white hover:bg-white/10"
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Previous
              </SecondaryButton>

              <div className="flex items-center space-x-1">
                {(() => {
                  let startPage = Math.max(1, currentPage - 2);
                  const endPage = Math.min(totalPages, startPage + 4);

                  if (endPage - startPage < 4 && startPage > 1) {
                    startPage = Math.max(1, endPage - 4);
                  }

                  const pageNumbers = [];

                  for (let i = startPage; i <= endPage; i++) {
                    pageNumbers.push(
                      <SecondaryButton
                        key={i}
                        size="sm"
                        onClick={() => paginate(i)}
                        className={
                          currentPage === i
                            ? "bg-amber-600 text-black hover:bg-white/90"
                            : "text-black border-white hover:bg-white/10"
                        }
                      >
                        {i}
                      </SecondaryButton>
                    );
                  }

                  return pageNumbers;
                })()}
              </div>

              <SecondaryButton
                variant="outline"
                size="sm"
                onClick={nextPage}
                disabled={currentPage === totalPages}
                className="text-white border-white hover:bg-white/10"
              >
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </SecondaryButton>
            </div>

            <div className="text-center text-sm text-white/70 mt-2">
              Showing {paginationMeta.from || 0}-{paginationMeta.to || 0} of {paginationMeta.total || 0} leads
            </div>
          </>
        )}
      </div>

      {editModalOpen && currentEditLead && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-2 sm:p-4 overflow-y-auto">
          <div
            ref={modalRef}
            className="bg-[#1c1b19] rounded-xl border border-zinc-600 w-full max-w-[96vw] h-[94vh] flex flex-col shadow-2xl"
          >
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between p-4 sm:p-6 border-b border-zinc-600">
              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-white flex items-center gap-2">
                  <TbEdit className="text-amber-500" />
                  {modalMode === "create" ? "Add Lead" : `Edit Lead #${currentEditLead.id}`}
                </h2>

                <p className="text-sm text-gray-400 mt-1">
                  {modalMode === "create"
                    ? "Create a new lead manually"
                    : `${currentEditLead.first_name || ""} ${currentEditLead.last_name || ""}`.trim() ||
                      currentEditLead.customer_email ||
                      "Lead details"}
                </p>
              </div>

              <button
                onClick={() => {
                  setEditModalOpen(false);
                  setCurrentEditLead(null);
                }}
                className="text-gray-400 hover:text-white self-end sm:self-auto"
              >
                <TbX className="w-7 h-7" />
              </button>
            </div>

            <div className="overflow-y-auto flex-1 p-4 sm:p-6">
              <div className="grid grid-cols-1 xl:grid-cols-4 gap-4">
                <div className="rounded-xl border border-zinc-700 bg-zinc-900/40 p-4">
                  <h3 className="text-lg font-semibold text-amber-500 mb-4 flex items-center gap-2">
                    <TbInfoCircle />
                    Lead Information
                  </h3>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-1">
                        Received At <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="datetime-local"
                        value={currentEditLead.received_at || ""}
                        onChange={(e) => updateCurrentLead("received_at", e.target.value)}
                        style={{ colorScheme: "dark" }}
                        className="w-full px-4 py-2 bg-transparent text-white border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-1">
                        Source <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={currentEditLead.source || ""}
                        onChange={(e) => updateCurrentLead("source", e.target.value)}
                        className="w-full px-4 py-2 bg-transparent text-white border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-1">Status</label>
                      <select
                        value={currentEditLead.status || "new"}
                        onChange={(e) => updateCurrentLead("status", e.target.value)}
                        className="w-full px-4 py-2 bg-transparent text-white border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                      >
                        {statusOptions.map((option) => (
                          <option key={option.value} value={option.value} className="bg-[#1c1b19]">
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-1">Customer Contacted At</label>
                      <input
                        type="datetime-local"
                        value={currentEditLead.customer_contacted_at || ""}
                        onChange={(e) => updateCurrentLead("customer_contacted_at", e.target.value)}
                        style={{ colorScheme: "dark" }}
                        className="w-full px-4 py-2 bg-transparent text-white border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                      />
                    </div>
                  </div>
                </div>

                <div className="rounded-xl border border-zinc-700 bg-zinc-900/40 p-4">
                  <h3 className="text-lg font-semibold text-blue-500 mb-4 flex items-center gap-2">
                    <TbUser />
                    Customer Details
                  </h3>

                  <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-1 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-400 mb-1">
                          First Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={currentEditLead.first_name || ""}
                          onChange={(e) => updateCurrentLead("first_name", e.target.value)}
                          className="w-full px-4 py-2 bg-transparent text-white border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-400 mb-1">
                          Last Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={currentEditLead.last_name || ""}
                          onChange={(e) => updateCurrentLead("last_name", e.target.value)}
                          className="w-full px-4 py-2 bg-transparent text-white border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-1">
                        Customer Email <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        value={currentEditLead.customer_email || ""}
                        onChange={(e) => updateCurrentLead("customer_email", e.target.value)}
                        className="w-full px-4 py-2 bg-transparent text-white border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-1">
                        Customer Phone <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={currentEditLead.customer_phone || ""}
                        onChange={(e) => updateCurrentLead("customer_phone", e.target.value)}
                        className="w-full px-4 py-2 bg-transparent text-white border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>

                <div className="rounded-xl border border-zinc-700 bg-zinc-900/40 p-4 xl:col-span-2">
                  <h3 className="text-lg font-semibold text-green-500 mb-4 flex items-center gap-2">
                    <TbRoute />
                    Route Details
                  </h3>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                    <div className="rounded-xl bg-black/20 border border-zinc-700 p-4">
                      <div className="flex items-center gap-2 mb-4">
                        <div className="w-2.5 h-2.5 rounded-full bg-blue-400"></div>
                        <h4 className="text-sm font-semibold text-blue-300 uppercase tracking-wide">Origin</h4>
                      </div>

                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-400 mb-1">
                            Origin City <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={currentEditLead.origin_city || ""}
                            onChange={(e) => updateCurrentLead("origin_city", e.target.value)}
                            className="w-full px-4 py-2 bg-transparent text-white border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                          />
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-400 mb-1">
                              State <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              value={currentEditLead.origin_state || ""}
                              onChange={(e) => updateCurrentLead("origin_state", e.target.value.toUpperCase())}
                              maxLength={2}
                              className="w-full px-4 py-2 bg-transparent text-white border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 uppercase"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-400 mb-1">
                              Zipcode <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              value={currentEditLead.origin_zip || ""}
                              onChange={(e) => updateCurrentLead("origin_zip", e.target.value)}
                              className="w-full px-4 py-2 bg-transparent text-white border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="rounded-xl bg-black/20 border border-zinc-700 p-4">
                      <div className="flex items-center gap-2 mb-4">
                        <div className="w-2.5 h-2.5 rounded-full bg-green-400"></div>
                        <h4 className="text-sm font-semibold text-green-300 uppercase tracking-wide">Destination</h4>
                      </div>

                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-400 mb-1">
                            Destination City <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={currentEditLead.destination_city || ""}
                            onChange={(e) => updateCurrentLead("destination_city", e.target.value)}
                            className="w-full px-4 py-2 bg-transparent text-white border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                          />
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-400 mb-1">
                              State <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              value={currentEditLead.destination_state || ""}
                              onChange={(e) => updateCurrentLead("destination_state", e.target.value.toUpperCase())}
                              maxLength={2}
                              className="w-full px-4 py-2 bg-transparent text-white border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 uppercase"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-400 mb-1">
                              Zipcode <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              value={currentEditLead.destination_zip || ""}
                              onChange={(e) => updateCurrentLead("destination_zip", e.target.value)}
                              className="w-full px-4 py-2 bg-transparent text-white border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="lg:col-span-2 rounded-xl bg-green-500/10 border border-green-500/30 p-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                        <div>
                          <p className="text-gray-400">Origin Summary</p>
                          <p className="text-white font-medium">
                            {currentEditLead.origin_city || "City"}, {currentEditLead.origin_state || "ST"}{" "}
                            {currentEditLead.origin_zip || "ZIP"}
                          </p>
                        </div>

                        <div>
                          <p className="text-gray-400">Destination Summary</p>
                          <p className="text-white font-medium">
                            {currentEditLead.destination_city || "City"}, {currentEditLead.destination_state || "ST"}{" "}
                            {currentEditLead.destination_zip || "ZIP"}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="rounded-xl border border-zinc-700 bg-zinc-900/40 p-4 xl:col-span-2">
                  <h3 className="text-lg font-semibold text-purple-500 mb-4 flex items-center gap-2">
                    <TbCar />
                    Vehicle Details
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-1">
                        Move Date <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="date"
                        value={currentEditLead.move_date || ""}
                        onChange={(e) => updateCurrentLead("move_date", e.target.value)}
                        style={{ colorScheme: "dark" }}
                        className="w-full px-4 py-2 bg-transparent text-white border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-1">
                        Vehicle Count <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={currentEditLead.vehicle_count || ""}
                        onChange={(e) => updateCurrentLead("vehicle_count", e.target.value)}
                        className="w-full px-4 py-2 bg-transparent text-white border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-1">
                        Vehicle Year <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={currentEditLead.vehicle_year || ""}
                        onChange={(e) => updateCurrentLead("vehicle_year", e.target.value)}
                        className="w-full px-4 py-2 bg-transparent text-white border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-1">
                        Vehicle Type <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={currentEditLead.vehicle_type || ""}
                        onChange={(e) => updateCurrentLead("vehicle_type", e.target.value)}
                        className="w-full px-4 py-2 bg-transparent text-white border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-1">
                        Vehicle Make <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={currentEditLead.vehicle_make || ""}
                        onChange={(e) => updateCurrentLead("vehicle_make", e.target.value)}
                        className="w-full px-4 py-2 bg-transparent text-white border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-1">
                        Vehicle Model <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={currentEditLead.vehicle_model || ""}
                        onChange={(e) => updateCurrentLead("vehicle_model", e.target.value)}
                        className="w-full px-4 py-2 bg-transparent text-white border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>

                    <div className="md:col-span-2 xl:col-span-3">
                      <label className="block text-sm font-medium text-gray-400 mb-1">
                        Vehicle Condition <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={currentEditLead.vehicle_condition || ""}
                        onChange={(e) => updateCurrentLead("vehicle_condition", e.target.value)}
                        className="w-full px-4 py-2 bg-transparent text-white border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                  </div>
                </div>

                <div className="rounded-xl border border-zinc-700 bg-zinc-900/40 p-4 xl:col-span-2">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
                    <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                      <TbCurrencyDollar className="text-amber-500" />
                      Quote Prices
                    </h3>

                    <button
                      type="button"
                      onClick={handleCalculateEditLeadPrices}
                      disabled={editPriceLoading}
                      className="inline-flex items-center justify-center gap-2 rounded-lg border border-green-500/40 bg-green-500/10 px-4 py-2 text-sm font-semibold text-green-300 transition hover:bg-green-500/20 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {editPriceLoading ? (
                        <span className="h-4 w-4 rounded-full border-2 border-green-300 border-t-transparent animate-spin" />
                      ) : (
                        <TbCalculator className="w-4 h-4" />
                      )}
                      {editPriceLoading ? "Calculating..." : "Calculate Automatically"}
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-green-300 mb-1">Low Price</label>
                      <div className="flex flex-col sm:flex-row gap-2">
                        <div className="relative flex-1">
                          <span className="absolute inset-y-0 left-3 flex items-center text-gray-400">$</span>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={currentEditLead.price_low || ""}
                            onChange={(e) => updateCurrentLead("price_low", e.target.value)}
                            className="w-full pl-8 pr-4 py-3 bg-transparent text-white border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                          />
                        </div>
                        <span className="sm:min-w-[82px] text-center text-xs px-3 py-3 rounded-lg bg-green-500/10 text-green-300 border border-green-500/30">
                          {editLeadPerMile.low !== null ? `$${formatNumber(editLeadPerMile.low)}/mi` : "N/A"}
                        </span>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-amber-300 mb-1">Medium Price</label>
                      <div className="flex flex-col sm:flex-row gap-2">
                        <div className="relative flex-1">
                          <span className="absolute inset-y-0 left-3 flex items-center text-gray-400">$</span>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={currentEditLead.price_medium || ""}
                            onChange={(e) => updateCurrentLead("price_medium", e.target.value)}
                            className="w-full pl-8 pr-4 py-3 bg-transparent text-white border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                          />
                        </div>
                        <span className="sm:min-w-[82px] text-center text-xs px-3 py-3 rounded-lg bg-amber-500/10 text-amber-300 border border-amber-500/30">
                          {editLeadPerMile.medium !== null ? `$${formatNumber(editLeadPerMile.medium)}/mi` : "N/A"}
                        </span>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-red-300 mb-1">High Price</label>
                      <div className="flex flex-col sm:flex-row gap-2">
                        <div className="relative flex-1">
                          <span className="absolute inset-y-0 left-3 flex items-center text-gray-400">$</span>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={currentEditLead.price_high || ""}
                            onChange={(e) => updateCurrentLead("price_high", e.target.value)}
                            className="w-full pl-8 pr-4 py-3 bg-transparent text-white border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                          />
                        </div>
                        <span className="sm:min-w-[82px] text-center text-xs px-3 py-3 rounded-lg bg-red-500/10 text-red-300 border border-red-500/30">
                          {editLeadPerMile.high !== null ? `$${formatNumber(editLeadPerMile.high)}/mi` : "N/A"}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="mt-5 rounded-xl bg-black/20 border border-zinc-700 p-4">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-4">
                      <p className="text-xs uppercase tracking-wide text-gray-500">Local price per mile preview</p>
                      <p className="text-xs text-gray-400">
                        {editDistanceLoading ? "Calculating distance for $/mi preview..." : editLeadDistanceForPreview ? `Using ${formatNumber(editLeadDistanceForPreview)} miles to calculate $/mi.` : "Distance unavailable for $/mi calculation."}
                      </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                      <div className="rounded-lg border border-green-500/20 bg-green-500/5 p-3">
                        <p className="text-gray-400">Low</p>
                        <p className="text-green-300 font-semibold">{formatCurrency(currentEditLead.price_low)}</p>
                        <p className="text-xs text-green-200/80 mt-1">
                          {editLeadPerMile.low !== null ? `$${formatNumber(editLeadPerMile.low)}/mi` : "N/A"}
                        </p>
                      </div>

                      <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-3">
                        <p className="text-gray-400">Medium</p>
                        <p className="text-amber-300 font-semibold">{formatCurrency(currentEditLead.price_medium)}</p>
                        <p className="text-xs text-amber-200/80 mt-1">
                          {editLeadPerMile.medium !== null ? `$${formatNumber(editLeadPerMile.medium)}/mi` : "N/A"}
                        </p>
                      </div>

                      <div className="rounded-lg border border-red-500/20 bg-red-500/5 p-3">
                        <p className="text-gray-400">High</p>
                        <p className="text-red-300 font-semibold">{formatCurrency(currentEditLead.price_high)}</p>
                        <p className="text-xs text-red-200/80 mt-1">
                          {editLeadPerMile.high !== null ? `$${formatNumber(editLeadPerMile.high)}/mi` : "N/A"}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="border-t border-zinc-600 p-4 sm:p-6">
              <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
                <SecondaryButton
                  variant="outline"
                  onClick={() => {
                    setEditModalOpen(false);
                    setCurrentEditLead(null);
                  }}
                  className="text-white border-white hover:bg-white/10 justify-center"
                >
                  Cancel
                </SecondaryButton>

                <SecondaryButton
                  onClick={handleSaveLead}
                  className="bg-amber-600 text-black hover:bg-amber-700 justify-center"
                  disabled={isSaving}
                >
                  {isSaving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black mr-2"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <TbCheck className="w-4 h-4 mr-2" />
                      {modalMode === "create" ? "Create Lead" : "Save Changes"}
                    </>
                  )}
                </SecondaryButton>
              </div>
            </div>
          </div>
        </div>
      )}

      {quoteModalOpen && quoteLead && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[1000] p-2 sm:p-4 overflow-y-auto">
          <div
            ref={quoteModalRef}
            className="bg-[#1c1b19] rounded-xl border border-zinc-600 w-full max-w-[96vw] h-[94vh] flex flex-col shadow-2xl"
          >
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between p-4 sm:p-6 border-b border-zinc-600">
              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-white flex items-center gap-2">
                  <TbCalculator className="text-amber-500" />
                  Automatic Quote Price
                </h2>
                <p className="text-sm text-gray-400 mt-1">
                  Lead #{quoteLead.id} • {buildLeadFullName(quoteLead) || quoteLead.customer_email}
                </p>
              </div>

              <button onClick={closeQuoteModal} className="text-gray-400 hover:text-white self-end sm:self-auto">
                <TbX className="w-7 h-7" />
              </button>
            </div>

            <div className="overflow-y-auto flex-1 p-4 sm:p-6">
              <div className="rounded-xl overflow-hidden border border-zinc-700 bg-zinc-900/60 mb-6">
                {buildEmbedDirectionsUrl() ? (
                  <iframe
                    title="Lead route map"
                    src={buildEmbedDirectionsUrl()}
                    className="w-full h-[260px] sm:h-[320px] lg:h-[380px] bg-zinc-900"
                    loading="lazy"
                    allowFullScreen
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                ) : (
                  <div className="w-full h-[260px] sm:h-[320px] lg:h-[380px] bg-zinc-900 flex items-center justify-center text-gray-400">
                    Loading map...
                  </div>
                )}

                {mapError && (
                  <div className="px-4 py-3 text-sm text-amber-300 bg-amber-500/10 border-t border-amber-500/30">
                    {mapError}
                  </div>
                )}
              </div>

              {quoteLoading ? (
                <div className="flex flex-col items-center justify-center py-16">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-amber-500"></div>
                  <p className="mt-4 text-gray-300 text-base">Calculating automatic quote price...</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {quoteResult?.manual_pricing_required && (
                    <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-4">
                      <div className="flex items-start gap-3">
                        <TbAlertCircle className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />
                        <div>
                          <h3 className="text-amber-300 font-semibold text-base">Manual pricing required</h3>
                          <p className="text-sm text-gray-300 mt-1">
                            The automatic calculation returned a manual pricing status. You can still enter the prices manually and save them.
                          </p>
                          {Array.isArray(quoteResult?.reasons) && quoteResult.reasons.length > 0 && (
                            <p className="text-xs text-gray-400 mt-2">Reason: {quoteResult.reasons.join(", ")}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {quotePricingDisclaimer && (
                    <div className="rounded-xl border border-blue-500/40 bg-blue-500/10 p-4">
                      <div className="flex items-start gap-3">
                        <TbInfoCircle className="w-5 h-5 text-blue-300 mt-0.5 flex-shrink-0" />
                        <p className="text-sm text-blue-100">{quotePricingDisclaimer}</p>
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    <div className="rounded-xl border border-zinc-700 bg-zinc-900/40 p-4">
                      <h3 className="text-lg font-semibold text-amber-500 mb-4 flex items-center gap-2">
                        <TbRoute />
                        Trip Details
                      </h3>

                      <div className="space-y-3 text-sm">
                        <div>
                          <p className="text-gray-400">Origin</p>
                          <p className="text-white font-medium">
                            {quoteLead.origin_city}, {quoteLead.origin_state} {quoteLead.origin_zip}
                          </p>
                        </div>

                        <div>
                          <p className="text-gray-400">Destination</p>
                          <p className="text-white font-medium">
                            {quoteLead.destination_city}, {quoteLead.destination_state} {quoteLead.destination_zip}
                          </p>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                          <div className="rounded-lg bg-black/20 border border-zinc-700 p-3">
                            <p className="text-gray-400">Distance</p>
                            <p className="text-white font-semibold">
                              {quoteDistanceData.effective_distance_text ||
                                quoteDistanceData.distance_text ||
                                `${formatNumber(quoteDistanceData.effective_distance_miles || quoteDistanceData.distance_miles)} mi`}
                            </p>
                          </div>

                          <div className="rounded-lg bg-black/20 border border-zinc-700 p-3">
                            <p className="text-gray-400">Duration</p>
                            <p className="text-white font-semibold">
                              {quoteDistanceData.effective_driver_duration_text ||
                                quoteDistanceData.driver_duration_text ||
                                quoteDistanceData.duration_text ||
                                "N/A"}
                            </p>
                          </div>
                        </div>

                        <div>
                          <p className="text-gray-400">Transport Type</p>
                          <p className="text-white font-medium">{quotePayload?.transport_type || "Open"}</p>
                        </div>
                      </div>
                    </div>

                    <div className="rounded-xl border border-zinc-700 bg-zinc-900/40 p-4">
                      <h3 className="text-lg font-semibold text-green-500 mb-4 flex items-center gap-2">
                        <TbCar />
                        Vehicle Details
                      </h3>

                      <div className="space-y-3 text-sm">
                        <div>
                          <p className="text-gray-400">Vehicle</p>
                          <p className="text-white font-medium">
                            {quoteLead.vehicle_year} {quoteLead.vehicle_make} {quoteLead.vehicle_model}
                          </p>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div className="rounded-lg bg-black/20 border border-zinc-700 p-3">
                            <p className="text-gray-400">Type</p>
                            <p className="text-white font-semibold">{quoteVehicle.type || quoteLead.vehicle_type || "N/A"}</p>
                          </div>

                          <div className="rounded-lg bg-black/20 border border-zinc-700 p-3">
                            <p className="text-gray-400">Condition</p>
                            <p className="text-white font-semibold">
                              {quoteVehicle.operable === 1 ? "Operable" : "Inoperable"}
                            </p>
                          </div>
                        </div>

                        <div>
                          <p className="text-gray-400">Original Lead Condition</p>
                          <p className="text-white font-medium">{quoteLead.vehicle_condition || "N/A"}</p>
                        </div>

                        <div>
                          <p className="text-gray-400">Vehicle Count</p>
                          <p className="text-white font-medium">{quoteLead.vehicle_count || "1"}</p>
                        </div>
                      </div>
                    </div>

                    <div className="rounded-xl border border-zinc-700 bg-zinc-900/40 p-4">
                      <h3 className="text-lg font-semibold text-blue-500 mb-4 flex items-center gap-2">
                        <TbUser />
                        Customer Details
                      </h3>

                      <div className="space-y-3 text-sm">
                        <div>
                          <p className="text-gray-400">Name</p>
                          <p className="text-white font-medium">{buildLeadFullName(quoteLead) || "N/A"}</p>
                        </div>

                        <div>
                          <p className="text-gray-400">Email</p>
                          <p className="text-white font-medium break-all">{quoteLead.customer_email || "N/A"}</p>
                        </div>

                        <div>
                          <p className="text-gray-400">Phone</p>
                          <p className="text-white font-medium">{quoteLead.customer_phone || "N/A"}</p>
                        </div>

                        <div>
                          <p className="text-gray-400">Move Date</p>
                          <p className="text-white font-medium">{formatDate(quoteLead.move_date)}</p>
                        </div>

                        <div>
                          <p className="text-gray-400">Source</p>
                          <p className="text-white font-medium">{quoteLead.source || "N/A"}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-xl border border-zinc-700 bg-zinc-900/40 p-4 sm:p-5">
                    <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                      <TbCurrencyDollar className="text-amber-500" />
                      Quote Prices
                    </h3>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-green-300 mb-1">Low Price</label>
                        <div className="flex gap-2 items-center">
                          <div className="relative flex-1">
                            <span className="absolute inset-y-0 left-3 flex items-center text-gray-400">$</span>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={quotePrices.low}
                              onChange={(e) => updateQuotePrice("low", e.target.value)}
                              className="w-full pl-8 pr-4 py-3 bg-transparent text-white border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                            />
                          </div>
                          <span className="min-w-[78px] text-center text-xs sm:text-sm px-3 py-3 rounded-lg bg-green-500/10 text-green-300 border border-green-500/30">
                            {quotePerMile.low !== null ? `$${formatNumber(quotePerMile.low)}/mi` : "N/A"}
                          </span>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-amber-300 mb-1">Medium Price</label>
                        <div className="flex gap-2 items-center">
                          <div className="relative flex-1">
                            <span className="absolute inset-y-0 left-3 flex items-center text-gray-400">$</span>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={quotePrices.medium}
                              onChange={(e) => updateQuotePrice("medium", e.target.value)}
                              className="w-full pl-8 pr-4 py-3 bg-transparent text-white border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                            />
                          </div>
                          <span className="min-w-[78px] text-center text-xs sm:text-sm px-3 py-3 rounded-lg bg-amber-500/10 text-amber-300 border border-amber-500/30">
                            {quotePerMile.medium !== null ? `$${formatNumber(quotePerMile.medium)}/mi` : "N/A"}
                          </span>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-red-300 mb-1">High Price</label>
                        <div className="flex gap-2 items-center">
                          <div className="relative flex-1">
                            <span className="absolute inset-y-0 left-3 flex items-center text-gray-400">$</span>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={quotePrices.high}
                              onChange={(e) => updateQuotePrice("high", e.target.value)}
                              className="w-full pl-8 pr-4 py-3 bg-transparent text-white border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                            />
                          </div>
                          <span className="min-w-[78px] text-center text-xs sm:text-sm px-3 py-3 rounded-lg bg-red-500/10 text-red-300 border border-red-500/30">
                            {quotePerMile.high !== null ? `$${formatNumber(quotePerMile.high)}/mi` : "N/A"}
                          </span>
                        </div>
                      </div>
                    </div>

                    <label className="mt-6 flex items-start gap-3 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={saveAsQuoteAndEmail}
                        onChange={(e) => setSaveAsQuoteAndEmail(e.target.checked)}
                        className="mt-1 h-5 w-5 rounded border-zinc-600 bg-transparent text-amber-600 focus:ring-amber-500"
                      />
                      <span className="text-sm sm:text-base text-gray-200">
                        Save as Quote and email prices to lead
                      </span>
                    </label>
                  </div>
                </div>
              )}
            </div>

            <div className="border-t border-zinc-600 p-4 sm:p-6">
              <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
                <SecondaryButton
                  variant="outline"
                  onClick={closeQuoteModal}
                  className="text-white border-white hover:bg-white/10 justify-center"
                  disabled={quoteSaving}
                >
                  Close
                </SecondaryButton>

                <SecondaryButton
                  onClick={handleSaveQuotePrice}
                  className="bg-amber-600 text-black hover:bg-amber-700 justify-center"
                  disabled={quoteLoading || quoteSaving}
                >
                  {quoteSaving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black mr-2"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <TbCheck className="w-4 h-4 mr-2" />
                      Save Price
                    </>
                  )}
                </SecondaryButton>
              </div>
            </div>
          </div>
        </div>
      )}

      {snackbar.open && <Snackbar message={snackbar.message} type={snackbar.type} onClose={closeSnackbar} duration={5000} />}
    </AuthenticatedLayout>
  );
}

export default Lead;